// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { useState } from 'react';
import { Typography, Collapse, IconButton } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import SVG from 'react-inlinesvg';
import sideBarIcon from 'Assets/sidebar-icon.svg';
import { useAppSelector } from 'Hooks';
import { getOrders } from 'Features/order/orderSelectors';
import stylesDef from './styles';

interface ProjectHeaderProps {
  onToggleCollapse?: () => void;
}

const ProjectHeader = ({ onToggleCollapse }: ProjectHeaderProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);

  const project = useAppSelector((state) => state.project.project);
  const order = useAppSelector((state) => getOrders(state).activeOrder);
  const folders = useAppSelector((state) => state.projectList.folders);

  const folderName = folders?.find((f) => f._id === project?.folderId)?.name;

  const [showDetails, setShowDetails] = useState(false);

  if (!project) return null;

  return (
    <Box sx={styles.container}>
      <Box sx={styles.headerRow}>
        <Typography sx={styles.projectName}>{project.name}</Typography>
        <IconButton size="small" onClick={onToggleCollapse} aria-label="Toggle sidebar">
          <SVG src={sideBarIcon} style={styles.sidebarIcon} />
        </IconButton>
      </Box>
      <Typography
        sx={styles.showMoreLink}
        onClick={() => setShowDetails(!showDetails)}
      >
        {showDetails ? 'Hide Project Details' : 'View Project Details'}
      </Typography>
      <Collapse in={showDetails}>
        <Box sx={styles.detailsContainer}>
          {project.referenceId && (
            <Box sx={styles.detailRow}>
              <Typography sx={styles.detailLabel}>Ref. ID</Typography>
              <Typography sx={styles.detailValue}>{project.referenceId}</Typography>
            </Box>
          )}
          {project.description && (
            <Box sx={styles.detailRow}>
              <Typography sx={styles.detailLabel}>Description</Typography>
              <Typography sx={styles.detailValue}>{project.description}</Typography>
            </Box>
          )}
          {project.type && (
            <Box sx={styles.detailRow}>
              <Typography sx={styles.detailLabel}>Type</Typography>
              <Typography sx={styles.detailValue}>{project.type}</Typography>
            </Box>
          )}
          {order?.middlePoint && Object.keys(order.middlePoint).length > 0 && (
            <>
              <Box sx={styles.detailRow}>
                <Typography sx={styles.detailLabel}>Location</Typography>
                <Typography sx={styles.detailValue}>
                  {`${order.middlePoint.place}, ${order.middlePoint.region} ${order.middlePoint.postcode}`}
                </Typography>
              </Box>
              <Box sx={styles.detailRow}>
                <Typography sx={styles.detailLabel}>Coordinates</Typography>
                <Typography sx={styles.detailValue}>
                  {`${order.middlePoint.longitude.toFixed(5)}, ${order.middlePoint.latitude.toFixed(5)}`}
                </Typography>
              </Box>
            </>
          )}
          {folderName && (
            <Box sx={styles.detailRow}>
              <Typography sx={styles.detailLabel}>Folder</Typography>
              <Typography sx={styles.detailValue}>{folderName}</Typography>
            </Box>
          )}
          <Box sx={styles.detailRow}>
            <Typography sx={styles.detailLabel}>
              {project.type === 'utilities' ? 'Project Length' : 'Acreage'}
            </Typography>
            <Typography sx={styles.detailValue}>
              {project.type === 'utilities'
                ? `${order?.kmlLength || 0} mi  (${order?.orderLinearFeet || 0} feet)`
                : `${order?.acreage || 0} acres`}
            </Typography>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default ProjectHeader;
